/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ExternalMCPHeaderDefinition,
  ExternalMCPHeaderDefinition$inboundSchema,
} from "./externalmcpheaderdefinition.js";
import {
  ExternalOAuthServer,
  ExternalOAuthServer$inboundSchema,
} from "./externaloauthserver.js";
import {
  FunctionEnvironmentVariable,
  FunctionEnvironmentVariable$inboundSchema,
} from "./functionenvironmentvariable.js";
import {
  OAuthEnablementMetadata,
  OAuthEnablementMetadata$inboundSchema,
} from "./oauthenablementmetadata.js";
import {
  PromptTemplate,
  PromptTemplate$inboundSchema,
} from "./prompttemplate.js";
import { Resource, Resource$inboundSchema } from "./resource.js";
import {
  SecurityVariable,
  SecurityVariable$inboundSchema,
} from "./securityvariable.js";
import {
  ServerVariable,
  ServerVariable$inboundSchema,
} from "./servervariable.js";
import { Tool, Tool$inboundSchema } from "./tool.js";
import { ToolsetOrigin, ToolsetOrigin$inboundSchema } from "./toolsetorigin.js";

/**
 * The network surfaces through which a Speakeasy-hosted MCP server may be reached.
 */
export const ToolsetNetworkAccessMode = {
  PublicOnly: "public_only",
  Dual: "dual",
  PrivateOnly: "private_only",
} as const;
/**
 * The network surfaces through which a Speakeasy-hosted MCP server may be reached.
 */
export type ToolsetNetworkAccessMode = ClosedEnum<
  typeof ToolsetNetworkAccessMode
>;

export type Toolset = {
  /**
   * The account type of the organization
   */
  accountType: string;
  /**
   * When the toolset was created.
   */
  createdAt: Date;
  /**
   * The ID of the custom domain to use for the toolset
   */
  customDomainId?: string | undefined;
  /**
   * A short url-friendly label that uniquely identifies a resource.
   */
  defaultEnvironmentSlug?: string | undefined;
  /**
   * Description of the toolset
   */
  description?: string | undefined;
  /**
   * The external MCP header definitions that are relevant to the toolset
   */
  externalMcpHeaderDefinitions?: Array<ExternalMCPHeaderDefinition> | undefined;
  externalOauthServer?: ExternalOAuthServer | undefined;
  /**
   * The function environment variables that are relevant to the toolset
   */
  functionEnvironmentVariables?: Array<FunctionEnvironmentVariable> | undefined;
  /**
   * The ID of the toolset
   */
  id: string;
  /**
   * Whether the toolset is enabled for MCP
   */
  mcpEnabled?: boolean | undefined;
  /**
   * Whether the toolset is public in MCP
   */
  mcpIsPublic?: boolean | undefined;
  /**
   * A short url-friendly label that uniquely identifies a resource.
   */
  mcpSlug?: string | undefined;
  /**
   * The name of the toolset
   */
  name: string;
  /**
   * The network surfaces through which a Speakeasy-hosted MCP server may be reached.
   */
  networkAccessMode?: ToolsetNetworkAccessMode | undefined;
  oauthEnablementMetadata: OAuthEnablementMetadata;
  /**
   * The organization ID this toolset belongs to
   */
  organizationId: string;
  origin?: ToolsetOrigin | undefined;
  /**
   * The project ID this toolset belongs to
   */
  projectId: string;
  /**
   * The prompt templates in this toolset -- Note: these are actual prompts, as in MCP prompts
   */
  promptTemplates: Array<PromptTemplate>;
  /**
   * The resource URNs in this toolset
   */
  resourceUrns: Array<string>;
  /**
   * The resources in this toolset
   */
  resources: Array<Resource>;
  /**
   * The security variables that are relevant to the toolset
   */
  securityVariables?: Array<SecurityVariable> | undefined;
  /**
   * The server variables that are relevant to the toolset
   */
  serverVariables?: Array<ServerVariable> | undefined;
  /**
   * A short url-friendly label that uniquely identifies a resource.
   */
  slug: string;
  /**
   * The mode to use for tool selection
   */
  toolSelectionMode: string;
  /**
   * The tool URNs in this toolset
   */
  toolUrns: Array<string>;
  /**
   * The id of the tool variations group enabling MCP tool filtering for this toolset. Set via toolsets.setToolVariationsGroup; null when filtering is disabled.
   */
  toolVariationsGroupId?: string | undefined;
  /**
   * The tools in this toolset
   */
  tools: Array<Tool>;
  /**
   * The version of the toolset (will be 0 if none exists)
   */
  toolsetVersion: number;
  /**
   * When the toolset was last updated.
   */
  updatedAt: Date;
  /**
   * The id of the user_session_issuer wired to this toolset. Set via toolsets.setUserSessionIssuer; null when no USI is linked.
   */
  userSessionIssuerId?: string | undefined;
  /**
   * A short url-friendly label that uniquely identifies a resource.
   */
  userSessionIssuerSlug?: string | undefined;
};

/** @internal */
export const ToolsetNetworkAccessMode$inboundSchema: z.ZodMiniEnum<
  typeof ToolsetNetworkAccessMode
> = z.enum(ToolsetNetworkAccessMode);

/** @internal */
export const Toolset$inboundSchema: z.ZodMiniType<Toolset, unknown> = z.pipe(
  z.object({
    account_type: z.string(),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    custom_domain_id: z.optional(z.string()),
    default_environment_slug: z.optional(z.string()),
    description: z.optional(z.string()),
    external_mcp_header_definitions: z.optional(
      z.array(ExternalMCPHeaderDefinition$inboundSchema),
    ),
    external_oauth_server: z.optional(ExternalOAuthServer$inboundSchema),
    function_environment_variables: z.optional(
      z.array(FunctionEnvironmentVariable$inboundSchema),
    ),
    id: z.string(),
    mcp_enabled: z.optional(z.boolean()),
    mcp_is_public: z.optional(z.boolean()),
    mcp_slug: z.optional(z.string()),
    name: z.string(),
    network_access_mode: z.optional(ToolsetNetworkAccessMode$inboundSchema),
    oauth_enablement_metadata: OAuthEnablementMetadata$inboundSchema,
    organization_id: z.string(),
    origin: z.optional(ToolsetOrigin$inboundSchema),
    project_id: z.string(),
    prompt_templates: z.array(PromptTemplate$inboundSchema),
    resource_urns: z.array(z.string()),
    resources: z.array(Resource$inboundSchema),
    security_variables: z.optional(z.array(SecurityVariable$inboundSchema)),
    server_variables: z.optional(z.array(ServerVariable$inboundSchema)),
    slug: z.string(),
    tool_selection_mode: z.string(),
    tool_urns: z.array(z.string()),
    tool_variations_group_id: z.optional(z.string()),
    tools: z.array(Tool$inboundSchema),
    toolset_version: z.int(),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    user_session_issuer_id: z.optional(z.string()),
    user_session_issuer_slug: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "account_type": "accountType",
      "created_at": "createdAt",
      "custom_domain_id": "customDomainId",
      "default_environment_slug": "defaultEnvironmentSlug",
      "external_mcp_header_definitions": "externalMcpHeaderDefinitions",
      "external_oauth_server": "externalOauthServer",
      "function_environment_variables": "functionEnvironmentVariables",
      "mcp_enabled": "mcpEnabled",
      "mcp_is_public": "mcpIsPublic",
      "mcp_slug": "mcpSlug",
      "network_access_mode": "networkAccessMode",
      "oauth_enablement_metadata": "oauthEnablementMetadata",
      "organization_id": "organizationId",
      "project_id": "projectId",
      "prompt_templates": "promptTemplates",
      "resource_urns": "resourceUrns",
      "security_variables": "securityVariables",
      "server_variables": "serverVariables",
      "tool_selection_mode": "toolSelectionMode",
      "tool_urns": "toolUrns",
      "tool_variations_group_id": "toolVariationsGroupId",
      "toolset_version": "toolsetVersion",
      "updated_at": "updatedAt",
      "user_session_issuer_id": "userSessionIssuerId",
      "user_session_issuer_slug": "userSessionIssuerSlug",
    });
  }),
);

export function toolsetFromJSON(
  jsonString: string,
): SafeParseResult<Toolset, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => Toolset$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'Toolset' from JSON`,
  );
}
